import { caseMatchesPlanFilter } from '~/utils/planDays'
import { isCaseWithoutStageTags } from '~/utils/caseTags'
import { fractionLabel } from '~/utils/companyCasesFilters'

/**
 * COMPANY CASES (/company-cases) — the case list's pure rules: the toolbar
 * filters (their defaults, "is any on", what each keeps) and the list order.
 * `stores/casesStore.js` holds the state and calls these. Extracted in the
 * /company-cases refactor (2026-10-05), behaviour unchanged.
 */

/** The toolbar filters (level 3) and their show-all defaults — the one list. */
export const makeCaseFilterDefaults = () => ({
  filterCasesInsuranceId: null,
  filterCasesStageTagId: null,
  filterCasesWithUnreadFollowUps: false,
})

export const CASE_FILTER_KEYS = Object.keys(makeCaseFilterDefaults())

/** The planning day's show-all default (level 2): `{ kind, iso }`. */
export const makePlanFilterDefault = () => ({ kind: 'all', iso: null })

/**
 * Is any toolbar filter on — anything but its `false` / `null` default? Drives
 * the empty state ("no cases match the filters" vs an empty day). The planning
 * day is deliberately not counted.
 */
export function hasActiveCaseFilters(filters) {
  return CASE_FILTER_KEYS.some(key => {
    const v = filters[key]

    return v != null && v !== false
  })
}

/**
 * The stage-tag filter's values: a stage tag id, `any` (has a stage tag),
 * `untagged` (has none), `unassigned` (nobody attends it), `assignee-<userId>`.
 */
function matchesStageTagFilter(c, value) {
  if (value === 'any') return (c.tags ?? []).some(t => t.tag?.category === 'stage')
  if (value === 'untagged') return isCaseWithoutStageTags(c)
  if (value === 'unassigned') return c.assignedTo?.id == null
  if (typeof value === 'string' && value.startsWith('assignee-')) {
    return String(c.assignedTo?.id) === value.slice('assignee-'.length)
  }

  return (c.tags ?? []).some(t => String(t.tag?.id) === String(value))
}

/**
 * The list as shown: the planning day AND every toolbar filter that's on, then
 * oldest start first (no start date sinks to the bottom).
 */
export function filterCompanyCases(cases, filters, planFilter) {
  let list = cases.filter(c => caseMatchesPlanFilter(c, planFilter))

  if (filters.filterCasesInsuranceId === 'any') {
    list = list.filter(c => (c.caseInsurances ?? []).length > 0)
  }
  else if (filters.filterCasesInsuranceId != null) {
    const wanted = String(filters.filterCasesInsuranceId)

    list = list.filter(c => (c.caseInsurances ?? []).some(ci => String(ci.insurance?.id) === wanted))
  }
  if (filters.filterCasesStageTagId != null) {
    list = list.filter(c => matchesStageTagFilter(c, filters.filterCasesStageTagId))
  }
  if (filters.filterCasesWithUnreadFollowUps) {
    list = list.filter(c => (c.unreadTrackingCount ?? 0) > 0)
  }

  return sortByStartedAt(list)
}

/** Oldest start first; cases without one sink to the bottom. Never mutates. */
export function sortByStartedAt(list) {
  return [...list].sort((a, b) => {
    const aT = a.startedAt ? new Date(a.startedAt).getTime() : Number.POSITIVE_INFINITY
    const bT = b.startedAt ? new Date(b.startedAt).getTime() : Number.POSITIVE_INFINITY

    return aT - bT
  })
}

/**
 * The company / lead-agency count on the scope bar (`casesCount` for a
 * company, `casesQuantity` for an agency) — `mine/total` when the viewed user
 * attends only some, else the total; null when the "Company case counts"
 * setting is off or there's no count.
 */
export function scopeCountLabel(entity, showCounts) {
  if (!showCounts) return null
  const total = entity?.casesCount ?? entity?.casesQuantity ?? null
  if (total == null) return null
  const mine = entity?.assignedCasesCount ?? entity?.assignedCasesQuantity ?? null

  return fractionLabel(total, mine)
}

/** The second, distinct badge: unread notes (always shown, whatever the setting). */
export const unreadBadgeOf = entity => entity?.unreadFollowUpsCount || null
